// CeKKeU 2.0 — Trends, Weekly Recap & Category Budgets.
// PURE, CLIENT-SIDE ONLY. Same transaction source as dailyHealth.js; never sends
// nominal to server. Independent from the FROZEN V2.1 resilience engine.

import { computeDailyHealth } from "@/lib/dailyHealth";
import { emergencyRunway } from "@/lib/financeMetrics";

const DAY_MS = 86400000;
const MONTHS_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const MONTHS_FULL = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

function d(x) {
  return x instanceof Date ? x : new Date(x);
}
function startOfDay(x) {
  const t = d(x);
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}
function startOfMonth(now) {
  const t = d(now);
  return new Date(t.getFullYear(), t.getMonth(), 1);
}
function daysInMonthOf(now) {
  const t = d(now);
  return new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
}
function mondayOf(x) {
  const t = startOfDay(x);
  const offset = (t.getDay() + 6) % 7;
  return new Date(t.getTime() - offset * DAY_MS);
}
function monthKey(dt) {
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
}
const sumAmt = (list) => list.reduce((s, t) => s + (Number(t.amount) || 0), 0);
const byCategory = (list) => {
  const m = {};
  list.forEach((t) => {
    const k = t.category || "Lainnya";
    m[k] = (m[k] || 0) + (Number(t.amount) || 0);
  });
  return m;
};

// ---------- Monthly trends (3 / 6 / 12 months) ----------
// Only returns months that actually exist in the user's history (no fabricated
// history before the first transaction).
export function monthlySeries(transactions, months = 3, now = new Date()) {
  const tx = (transactions || []).filter((t) => {
    const dt = d(t.date);
    return !isNaN(dt.getTime());
  });
  if (tx.length === 0) return { series: [], monthsAvailable: 0, requested: months, insufficient: true, totalHistory: 0 };

  const map = {};
  tx.forEach((t) => {
    const k = monthKey(d(t.date));
    if (!map[k]) map[k] = { income: 0, expense: 0 };
    if (t.type === "income") map[k].income += Number(t.amount) || 0;
    else map[k].expense += Number(t.amount) || 0;
  });

  const firstDate = new Date(Math.min(...tx.map((t) => +d(t.date))));
  const cur = startOfMonth(now);
  const first = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
  const totalHistory = (cur.getFullYear() - first.getFullYear()) * 12 + (cur.getMonth() - first.getMonth()) + 1;
  const monthsAvailable = Math.max(1, Math.min(totalHistory, months));

  const showYear = months >= 12;
  const series = [];
  for (let i = monthsAvailable - 1; i >= 0; i--) {
    const dd = new Date(cur.getFullYear(), cur.getMonth() - i, 1);
    const v = map[monthKey(dd)] || { income: 0, expense: 0 };
    series.push({
      key: monthKey(dd),
      label: MONTHS_ID[dd.getMonth()] + (showYear ? `'${String(dd.getFullYear()).slice(2)}` : ""),
      income: v.income,
      expense: v.expense,
      net: v.income - v.expense,
    });
  }
  return { series, monthsAvailable, requested: months, insufficient: totalHistory < months, totalHistory };
}

// ---------- Weekly recap (last COMPLETE Mon–Sun week) ----------
export function weeklyRecap(finance, now = new Date()) {
  const tx = (finance?.transactions || []).filter((t) => t.type === "income" || t.type === "expense");
  if (tx.length === 0) return { available: false };

  const curMon = mondayOf(now);
  const lastStart = new Date(curMon.getTime() - 7 * DAY_MS); // Monday of last week
  const lastEnd = new Date(curMon.getTime() - DAY_MS); // Sunday of last week
  const prevStart = new Date(lastStart.getTime() - 7 * DAY_MS);
  const inRange = (t, s, e) => {
    const dt = d(t.date);
    return dt >= s && dt < e;
  };

  const firstDate = startOfDay(Math.min(...tx.map((t) => +d(t.date))));
  const lastTx = tx.filter((t) => inRange(t, lastStart, curMon));
  const prevTx = tx.filter((t) => inRange(t, prevStart, lastStart));

  // No activity in the last complete week → don't show a misleading Rp0 recap.
  if (lastTx.length === 0) return { available: false, noActivity: lastStart >= firstDate };

  const income = sumAmt(lastTx.filter((t) => t.type === "income"));
  const expense = sumAmt(lastTx.filter((t) => t.type === "expense"));
  const net = income - expense;

  const catMap = byCategory(lastTx.filter((t) => t.type === "expense"));
  let topCategory = null;
  Object.keys(catMap).forEach((c) => {
    if (!topCategory || catMap[c] > topCategory.amount) topCategory = { name: c, amount: catMap[c] };
  });

  const prevExpense = sumAmt(prevTx.filter((t) => t.type === "expense"));
  const hasPrev = prevTx.length > 0 || prevStart >= firstDate;
  const changePct = hasPrev && prevExpense > 0 ? expense / prevExpense - 1 : null;
  const baselineLearning = !hasPrev;

  // Build up to 3 human, non-alarming insights (single coherent narrative first — no contradictions).
  const insights = [];
  if (net < 0) {
    let txt = `Minggu lalu masih defisit ${rp(-net)} (pengeluaran lebih besar dari pemasukan).`;
    if (changePct != null && changePct <= -0.15)
      txt = `Pengeluaran turun ${Math.round(-changePct * 100)}% dari minggu sebelumnya — arah yang bagus. Namun minggu lalu masih defisit ${rp(-net)}, jadi tetap perlu direm.`;
    else if (changePct != null && changePct >= 0.15)
      txt = `Minggu lalu defisit ${rp(-net)} dan pengeluaran naik ${Math.round(changePct * 100)}% dari minggu sebelumnya. Coba rem sedikit minggu ini.`;
    insights.push({ level: "warning", text: txt });
  } else {
    let txt = `Minggu lalu surplus ${rp(net)}. Sisihkan sebagian untuk dana darurat.`;
    if (changePct != null && changePct <= -0.15)
      txt = `Surplus ${rp(net)} dan pengeluaran turun ${Math.round(-changePct * 100)}% dari minggu sebelumnya — kerja bagus!`;
    insights.push({ level: "positive", text: txt });
  }

  if (topCategory) insights.push({ level: "info", text: `Pengeluaran terbesar di "${topCategory.name}" (${rp(topCategory.amount)}).` });

  return {
    available: true,
    weekStart: lastStart,
    weekEnd: lastEnd,
    income,
    expense,
    net,
    topCategory,
    prevExpense,
    changePct,
    baselineLearning,
    insights: insights.slice(0, 3),
  };
}

// Monday (start) of the CURRENT week — used as the "new week" recap key.
export function currentWeekKey(now = new Date()) {
  return mondayOf(now).toISOString().slice(0, 10);
}

// ---------- Gentle logging reminder (in-app only, client-side) ----------
// No warning after 1 quiet day; a light nudge only after >=2 days without a new
// entry. Once acknowledged (or a tx logged) today, it stays hidden for the day.
export function logReminder(finance, now = new Date()) {
  const tx = (finance?.transactions || []).filter((t) => t && t.date);
  const todayISO = d(now).toISOString().slice(0, 10);
  if (!tx.length) return { show: false, daysSince: null, todayISO };
  const lastISO = tx.map((t) => String(t.date).slice(0, 10)).sort().pop();
  const daysSince = Math.round((Date.parse(todayISO) - Date.parse(lastISO)) / DAY_MS);
  const ackToday = (finance?.ui?.reminderAckDate || null) === todayISO;
  return { show: daysSince >= 2 && !ackToday, daysSince, lastISO, todayISO };
}

// ---------- Category budget status (monthly, resets by calendar month) ----------
export function categoryBudgetStatus(finance, now = new Date()) {
  const budgets = finance?.budgets || [];
  if (!budgets.length) return [];
  const tx = finance?.transactions || [];
  const som = startOfMonth(now);
  const dim = daysInMonthOf(now);
  const day = d(now).getDate();
  const daysElapsed = day;
  const daysRemaining = Math.max(dim - day, 0);

  const monthExp = tx.filter((t) => {
    const dt = d(t.date);
    return t.type === "expense" && dt >= som && dt <= d(now);
  });
  const allExpAmts = tx.filter((t) => t.type === "expense").map((t) => Number(t.amount) || 0).sort((a, b) => a - b);
  const median = allExpAmts.length ? allExpAmts[Math.floor(allExpAmts.length / 2)] : 0;
  const incTh = median > 0 ? median * 4 : Infinity; // exclude one-off big spends from the pace projection

  return budgets.map((b) => {
    const target = Number(b.amount) || 0;
    const catTx = monthExp.filter((t) => t.category === b.category);
    const used = sumAmt(catTx);
    const regular = catTx.filter((t) => (Number(t.amount) || 0) <= incTh);
    const regularRate = daysElapsed > 0 ? sumAmt(regular) / daysElapsed : 0;
    const projectedEnd = used + regularRate * daysRemaining;
    const remaining = target - used;
    const pctUsed = target > 0 ? used / target : 0;
    const monthPct = daysElapsed / dim;

    let level = "ok";
    let message = `Aman, terpakai ${Math.round(pctUsed * 100)}% dari target.`;
    if (used > target) {
      level = "danger";
      message = `Sudah melewati target sebesar ${rp(used - target)}.`;
    } else if (target > 0 && projectedEnd > target * 1.05) {
      level = "warning";
      message = `Terpakai ${Math.round(pctUsed * 100)}%, sementara bulan baru berjalan ${Math.round(monthPct * 100)}%. Jika berlanjut, perkiraan akhir bulan ${rp(projectedEnd)} (${rp(projectedEnd - target)} di atas target).`;
    }

    return { id: b.id, category: b.category, target, used, remaining, pctUsed, projectedEnd, daysRemaining, level, message };
  });
}

// ---------- Monthly summary (comprehensive, connects all data sources) ----------
function startOfMonthOffset(now, offset) {
  const t = d(now);
  return new Date(t.getFullYear(), t.getMonth() - offset, 1);
}
function daysInMonthAt(som) {
  return new Date(som.getFullYear(), som.getMonth() + 1, 0).getDate();
}

// Average Daily Health score across the month (samples every ~3 days).
function averageMonthHealth(finance, som, isCurrent, now) {
  const y = som.getFullYear();
  const m = som.getMonth();
  const lastDay = isCurrent ? d(now).getDate() : daysInMonthAt(som);
  const days = new Set();
  for (let dd = 3; dd <= lastDay; dd += 3) days.add(dd);
  days.add(lastDay);
  let total = 0;
  let count = 0;
  days.forEach((dd) => {
    const ref = new Date(y, m, dd, 12, 0, 0);
    const h = computeDailyHealth(finance, ref);
    if (h.status === "ready" && h.score) {
      total += h.score.value;
      count += 1;
    }
  });
  if (!count) return null;
  const value = Math.round(total / count);
  return { value, band: value >= 72 ? "sehat" : value >= 50 ? "waspada" : "kritis" };
}

export function monthsAvailable(transactions, now = new Date()) {
  const tx = (transactions || []).filter((t) => !isNaN(d(t.date).getTime()));
  if (!tx.length) return 1;
  const first = new Date(Math.min(...tx.map((t) => +d(t.date))));
  const cur = startOfMonth(now);
  return (cur.getFullYear() - first.getFullYear()) * 12 + (cur.getMonth() - first.getMonth()) + 1;
}

export function monthlySummary(finance, offset = 0, now = new Date()) {
  const tx = (finance?.transactions || []).filter((t) => !isNaN(d(t.date).getTime()));
  const som = startOfMonthOffset(now, offset);
  const nextSom = new Date(som.getFullYear(), som.getMonth() + 1, 1);
  const prevSom = new Date(som.getFullYear(), som.getMonth() - 1, 1);
  const isCurrentMonth = offset === 0;
  const monthLabel = `${MONTHS_FULL[som.getMonth()]} ${som.getFullYear()}`;

  const inMonth = (t, s, e) => {
    const dt = d(t.date);
    return dt >= s && dt < e;
  };
  const monthTx = tx.filter((t) => inMonth(t, som, nextSom));
  const prevTx = tx.filter((t) => inMonth(t, prevSom, som));

  const monthExp = monthTx.filter((t) => t.type === "expense");
  const income = sumAmt(monthTx.filter((t) => t.type === "income"));
  const expense = sumAmt(monthExp);
  const net = income - expense;
  const savingRate = income > 0 ? net / income : null;

  const hasData = monthTx.length > 0;

  const firstTx = tx.length ? startOfDay(Math.min(...tx.map((t) => +d(t.date)))) : null;
  const hasPrev = prevTx.length > 0 || (firstTx && firstTx < som);
  const prevExpense = sumAmt(prevTx.filter((t) => t.type === "expense"));
  const expenseChangePct = hasPrev && prevExpense > 0 ? expense / prevExpense - 1 : null;

  const catMap = byCategory(monthExp);
  const prevCatMap = byCategory(prevTx.filter((t) => t.type === "expense"));
  const topCategories = Object.keys(catMap)
    .map((name) => ({ name, amount: catMap[name], pct: expense > 0 ? catMap[name] / expense : 0 }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  const budgets = finance?.budgets || [];
  const budgetRows = budgets.map((b) => {
    const used = catMap[b.category] || 0;
    const target = Number(b.amount) || 0;
    return { category: b.category, target, used, over: used - target };
  });
  const overBudget = budgetRows.filter((b) => b.target > 0 && b.over > 0).sort((a, b) => b.over - a.over);

  let patternChange = null;
  if (hasPrev) {
    const material = Math.max(200000, 0.05 * (expense || 0));
    Object.keys({ ...catMap, ...prevCatMap }).forEach((cat) => {
      const delta = (catMap[cat] || 0) - (prevCatMap[cat] || 0);
      if (Math.abs(delta) >= material && (!patternChange || Math.abs(delta) > Math.abs(patternChange.delta))) {
        patternChange = { category: cat, delta, pct: prevCatMap[cat] > 0 ? delta / prevCatMap[cat] : null };
      }
    });
  }

  const avgHealth = hasData ? averageMonthHealth(finance, som, isCurrentMonth, now) : null;
  const emergency = Number(finance?.emergencyFund?.current_amount) || 0;
  // Emergency runway via the SINGLE SOURCE OF TRUTH (Emergency ÷ Essential Monthly).
  const _rw = emergencyRunway(finance, now);
  const emergencyMonths = _rw.months; // number | null (null = not computable / no fund)
  const monthlyDebt = (finance?.debts || []).filter((x) => x.active !== false).reduce((s, x) => s + (Number(x.monthly_payment) || 0), 0);
  // Debt ratio counts actual "Cicilan" spending this month (how users log it) plus any configured debt payments.
  const debtSpend = catMap["Cicilan"] || 0;
  const debtRatio = income > 0 ? (monthlyDebt + debtSpend) / income : null;

  let projectionNext = null;
  if (isCurrentMonth) {
    const h = computeDailyHealth(finance, now);
    if (h.status === "ready") projectionNext = h.forecast.projectedEndBalance;
  } else if (income > 0) {
    projectionNext = net;
  }

  let level = "sehat";
  if (net < 0 || (savingRate != null && savingRate < 0) || (avgHealth && avgHealth.band === "kritis") || (debtRatio != null && debtRatio > 0.5))
    level = "perhatian";
  else if ((savingRate != null && savingRate < 0.1) || overBudget.length >= 2 || (avgHealth && avgHealth.band === "waspada") || (debtRatio != null && debtRatio > 0.35))
    level = "waspada";
  const statusLabel = level === "sehat" ? "Sehat" : level === "waspada" ? "Waspada" : "Perlu Perhatian";

  const insights = [];
  if (expenseChangePct != null && Math.abs(expenseChangePct) >= 0.05) {
    const dir = expenseChangePct > 0 ? "naik" : "turun";
    insights.push(`Pengeluaran bulan ini ${rp(expense)}, ${dir} ${Math.round(Math.abs(expenseChangePct) * 100)}% dari bulan lalu.`);
  }
  if (patternChange) {
    const dir = patternChange.delta > 0 ? "naik" : "turun";
    const pctStr = patternChange.pct != null ? ` (${Math.round(Math.abs(patternChange.pct) * 100)}%)` : "";
    insights.push(`Pengeluaran ${patternChange.category} ${dir} ${rp(Math.abs(patternChange.delta))}${pctStr} dibanding bulan lalu.`);
  }
  if (overBudget.length === 1) insights.push(`Pengeluaran ${overBudget[0].category} melewati target ${rp(overBudget[0].over)}.`);
  else if (overBudget.length >= 2) insights.push(`${overBudget.length} kategori melewati target bulan ini.`);
  if (insights.length < 3 && savingRate != null && savingRate >= 0.1)
    insights.push(`Anda berhasil menyisihkan ${Math.round(savingRate * 100)}% pemasukan (${rp(net)}) bulan ini.`);
  if (insights.length < 3 && net < 0)
    insights.push(`Bulan ini defisit ${rp(-net)} — pengeluaran lebih besar dari pemasukan.`);

  // Recommendations follow the actual financial condition (#3-G). When cash flow
  // is negative, stabilizing it comes first — a savings target is NOT the headline.
  const recommendations = [];
  if (net < 0) {
    recommendations.push("Prioritas utama: stabilkan arus kas — usahakan pengeluaran bulan depan tidak melebihi pemasukan.");
    if (topCategories.length)
      recommendations.push(`Pengeluaran terbesar ada di ${topCategories[0].name} (${Math.round(topCategories[0].pct * 100)}% dari total pengeluaran). Tinjau bagian ini lebih dulu.`);
    if (overBudget.length >= 1)
      recommendations.push(`Tekan ${overBudget[0].category} sekitar ${rp(overBudget[0].over)} agar kembali sesuai target.`);
    else if (recommendations.length < 3)
      recommendations.push("Kurangi dulu pengeluaran yang bisa ditunda (hiburan, langganan, belanja non-wajib) sampai arus kas kembali positif.");
    if (recommendations.length < 3 && debtRatio != null && debtRatio > 0.4)
      recommendations.push(`Beban cicilan sekitar ${Math.round(debtRatio * 100)}% dari pemasukan tergolong berat — tinjau ulang komitmen tetap bila memungkinkan.`);
  } else {
    if (overBudget.length >= 1)
      recommendations.push(`Bulan depan, coba tekan ${overBudget[0].category} sekitar ${rp(overBudget[0].over)} agar kembali sesuai target.`);
    if (_rw.available && emergencyMonths != null && emergencyMonths < 3)
      recommendations.push("Kuatkan dana darurat hingga minimal 3 bulan pengeluaran wajib sebagai penyangga.");
    else if (!_rw.hasFund)
      recommendations.push("Mulai sisihkan dana darurat — target awal setara 1 bulan pengeluaran wajib.");
    if (recommendations.length < 3 && savingRate != null && savingRate < 0.1 && income > 0)
      recommendations.push(`Targetkan menyisihkan minimal 10% pemasukan (~${rp(Math.abs(0.1 * income))}) bulan depan.`);
    if (recommendations.length < 3 && debtRatio != null && debtRatio > 0.35)
      recommendations.push(`Rasio cicilan ${Math.round(debtRatio * 100)}% dari pemasukan — prioritaskan lunasi yang berbunga tinggi.`);
    if (recommendations.length < 3 && projectionNext != null && projectionNext > 0)
      recommendations.push(`Jika pola ini dipertahankan, ${isCurrentMonth ? "akhir bulan" : "bulan depan"} Anda berpotensi menyisakan sekitar ${rp(projectionNext)}.`);
    if (recommendations.length === 0)
      recommendations.push("Pertahankan pola belanja Anda dan sisihkan surplus untuk dana darurat.");
  }

  return {
    monthLabel,
    isCurrentMonth,
    hasData,
    status: { level, label: statusLabel },
    income,
    expense,
    net,
    savingRate,
    prev: { available: hasPrev, expense: prevExpense, changePct: expenseChangePct },
    topCategories,
    budgetRows,
    overBudget,
    patternChange,
    avgHealth,
    emergency: { amount: emergency, months: emergencyMonths, available: _rw.available, basis: _rw.basis },
    debtRatio,
    monthlyDebt,
    projectionNext,
    insights: insights.slice(0, 3),
    recommendations: recommendations.slice(0, 3),
    totalHistory: monthsAvailable(tx, now),
    offset,
  };
}

// ---------- Logging streak (consecutive days with at least one entry) ----------
export function loggingStreak(finance, now = new Date()) {
  const tx = (finance?.transactions || []).filter((t) => t && t.date);
  if (!tx.length) return { current: 0, longest: 0, loggedToday: false };
  const daySet = new Set(tx.map((t) => String(t.date).slice(0, 10)));
  const days = Array.from(daySet).sort(); // ascending YYYY-MM-DD

  // longest run of consecutive days
  let longest = 1;
  let run = 1;
  for (let i = 1; i < days.length; i++) {
    const gap = Math.round((Date.parse(days[i]) - Date.parse(days[i - 1])) / DAY_MS);
    if (gap === 1) run += 1;
    else run = 1;
    if (run > longest) longest = run;
  }

  // current streak counts back from today (or yesterday if today has no entry)
  const todayISO = d(now).toISOString().slice(0, 10);
  const yestISO = d(+d(now) - DAY_MS).toISOString().slice(0, 10);
  let current = 0;
  let cursor;
  if (daySet.has(todayISO)) cursor = todayISO;
  else if (daySet.has(yestISO)) cursor = yestISO;
  else cursor = null;
  while (cursor && daySet.has(cursor)) {
    current += 1;
    cursor = d(Date.parse(cursor) - DAY_MS).toISOString().slice(0, 10);
  }

  return { current, longest: Math.max(longest, current), loggedToday: daySet.has(todayISO) };
}

// local compact Rupiah (no UI deps)
function rp(v) {
  const n = Math.round(Math.abs(Number(v) || 0));
  let out;
  if (n >= 1e6) out = trim(n / 1e6) + " jt";
  else if (n >= 1e3) out = trim(n / 1e3) + " rb";
  else out = String(n);
  return "Rp" + out;
}
function trim(x) {
  const r = Math.round(x * 10) / 10;
  return (Number.isInteger(r) ? String(r) : r.toFixed(1)).replace(".", ",");
}